import {
  defer,
  distinctUntilChanged,
  fromEvent,
  map,
  Observable,
  shareReplay,
  startWith,
} from 'rxjs';

type DocumentWithHidden = Document & {
  hidden?: boolean;
  msHidden?: boolean;
  webkitHidden?: boolean;
  visibilityState?: DocumentVisibilityState;
};

/**
 * Creates an observable that emits `true` when the tab is visible, `false` when hidden.
 *
 * Supports the standard Page Visibility API plus vendor-prefixed fallbacks
 * (`msHidden`, `webkitHidden`). Multicasts with `shareReplay` so multiple
 * subscribers share a single event listener.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
 */
export const createPageVisibilityObservable = (): Observable<boolean> => {
  const doc = document as DocumentWithHidden;
  const { hiddenProperty, visibilityEvent } = resolveVisibilityApi(doc);

  const isDocumentVisible = (): boolean => {
    if (doc.visibilityState !== undefined) {
      return doc.visibilityState === 'visible';
    }
    return !doc[hiddenProperty];
  };

  return defer(() =>
    fromEvent(document, visibilityEvent).pipe(
      map(() => isDocumentVisible()),
      startWith(isDocumentVisible())
    )
  ).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
};

const resolveVisibilityApi = (
  doc: DocumentWithHidden
): { hiddenProperty: 'hidden' | 'msHidden' | 'webkitHidden'; visibilityEvent: string } => {
  if (typeof doc.msHidden !== 'undefined') {
    return { hiddenProperty: 'msHidden', visibilityEvent: 'msvisibilitychange' };
  }
  if (typeof doc.webkitHidden !== 'undefined') {
    return { hiddenProperty: 'webkitHidden', visibilityEvent: 'webkitvisibilitychange' };
  }
  return { hiddenProperty: 'hidden', visibilityEvent: 'visibilitychange' };
};
